import { type Level } from '@tiptap/extension-heading';
import { Fragment, type Node, Slice } from '@tiptap/pm/model';
import { type Editor, generateJSON } from '@tiptap/react';
import { marked } from 'marked';

import { getExtensions } from '.';
import { InternalServerError } from '../../error';
import { trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';

export function addLink(editor: Editor, text: string | null, link: string, isTargetBlank: boolean) {
  if (editor == null) {
    return false;
  }
  const linkAttrs = isTargetBlank
    ? { href: link, target: '_blank', rel: null }
    : { target: null, rel: null, href: link };
  if (editor.state.selection.empty) {
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'text',
        text: text ?? '',
        marks: [
          {
            type: 'link',
            attrs: linkAttrs,
          },
        ],
      })
      .run();
    return true;
  }
  editor.chain().focus().extendMarkRange('link').setLink(linkAttrs).run();
  return true;
}

export function countLink(editor: Editor) {
  if (editor == null) {
    return 0;
  }
  const linkRanges = new Map();
  let currentLink: { href: string; startPos: number; endPos: number } | null = null;
  editor.state.doc.descendants(function (node, pos) {
    const linkMark = node.marks.find((mark) => mark.type.name === 'link');
    if (linkMark == null) {
      currentLink = null;
      return;
    }
    const { href } = linkMark.attrs;
    if (currentLink?.href === href && currentLink?.endPos === pos) {
      currentLink.endPos = pos + node.nodeSize;
      return;
    }
    currentLink = {
      href: href as string,
      startPos: pos,
      endPos: pos + node.nodeSize,
    };
    if (!linkRanges.has(href)) {
      linkRanges.set(href, []);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const linkMap = linkRanges.get(href);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (linkMap?.push != null) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      linkMap.push(currentLink);
    }
  });
  let linksCount = 0;
  for (const linkRange of linkRanges) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const length = linkRange?.[1]?.length;
    if (length == null) {
      continue;
    }

    linksCount += length;
  }
  return linksCount;
}

export function toggleAlignment(
  editor: Editor,
  alignment: 'justify' | 'center' | 'right' | 'left'
) {
  if (editor.isActive({ textAlign: alignment })) {
    editor.chain().focus().unsetTextAlign().run();
    return;
  }
  editor.chain().focus().setTextAlign(alignment).run();
}

export function setElement(editor: Editor, element: 'paragraph' | 'heading', level?: Level) {
  if (element === 'paragraph') {
    editor.commands.setParagraph();
    return;
  }
  editor.commands.setHeading({ level: level ?? 1 });
}

export function upsertImage(
  editor: Editor,
  args: { src: string; alt?: string; nodePosition?: number }
) {
  const { src, alt, nodePosition } = args;
  if (editor == null) {
    return;
  }
  if (typeof nodePosition !== 'number') {
    editor.chain().focus().setImage({ src, alt }).run();
    return;
  }
  editor
    .chain()
    .command(function (args) {
      const { tr } = args;
      if (editor.schema.nodes.image == null) {
        return false;
      }
      const newImage = editor.schema.nodes.image.create({ src, alt });
      tr.replaceWith(nodePosition, nodePosition + 1, newImage);
      return true;
    })
    .run();
}

export function addParagraphBelow(editor: Editor, newParagraphContent: string) {
  const { parent } = editor.view.state.selection.$to;
  let parentPos: number | null = null;
  editor.state.doc.descendants(function (node, pos) {
    if (node === parent) {
      parentPos = pos;
      return false;
    }
  });
  if (typeof parentPos !== 'number') {
    return;
  }
  const addPos = (parentPos as number) + parent.nodeSize;
  const docSizeBefore = editor.state.doc.content.size;
  editor
    .chain()
    .insertContentAt(addPos, marked.parse(newParagraphContent, { gfm: true }))
    .focus(addPos)
    .run();
  const docSizeAfter = editor.state.doc.content.size;
  const insertedSize = docSizeAfter - docSizeBefore;
  return {
    from: addPos,
    to: addPos + insertedSize,
  };
}

export async function replaceSelectionInParagraph(
  editor: Editor,
  replacement: string,
  textSelectionRange: { from: number; to: number } | null
) {
  const { from, to } = textSelectionRange ?? {
    from: editor.view.state.selection.from,
    to: editor.view.state.selection.to,
  };

  const { doc } = editor.view.state;
  let paragraphStart = from;
  let paragraphEnd = to;

  // find the paragraph where the selection is in
  doc.nodesBetween(0, doc.content.size, function (node, pos) {
    if (node.isBlock) {
      const nodeStart = pos;
      const nodeEnd = pos + node.nodeSize;

      if (nodeStart <= from && nodeEnd >= to) {
        paragraphStart = nodeStart;
        paragraphEnd = nodeEnd;
        return false;
      }
    }
  });

  const beforeSelection = doc.textBetween(paragraphStart, from);
  const afterSelection = doc.textBetween(to, paragraphEnd);

  const newContent = beforeSelection + replacement + afterSelection;
  const parsed = await marked.parse(newContent, { gfm: true });
  const docSizeBefore = editor.state.doc.content.size;
  editor
    .chain()
    .command(function (args) {
      const { tr } = args;
      tr.deleteRange(paragraphStart, paragraphEnd);
      const fragment = generateJSON(
        parsed,
        getExtensions(() => {})
      );
      const node = editor.schema.nodeFromJSON(fragment);
      tr.insert(paragraphStart, node.content);
      return true;
    })
    .run();
  const docSizeAfter = editor.state.doc.content.size;
  const insertedSize = docSizeAfter - docSizeBefore;
  const originalParagraphSize = paragraphEnd - paragraphStart;
  const newContentSize = originalParagraphSize + insertedSize;
  return {
    from: paragraphStart,
    to: paragraphStart + newContentSize,
  };
}

export async function replaceSelection(
  editor: Editor,
  replacement: string,
  textSelectionRange: { from: number; to: number } | null
) {
  const { from, to } = textSelectionRange ?? {
    from: editor.view.state.selection.from,
    to: editor.view.state.selection.to,
  };

  const $from = editor.state.doc.resolve(from);
  const $to = editor.state.doc.resolve(to);

  const blockRange = $from.blockRange($to);
  if (blockRange == null) {
    return replaceSelectionInParagraph(editor, replacement, { from, to });
  }

  const blockStart = blockRange.start;
  const blockEnd = blockRange.end;

  const beforeSelection = editor.state.doc.textBetween(blockStart, from);
  const afterSelection = editor.state.doc.textBetween(to, blockEnd);

  const newContent = beforeSelection + replacement + afterSelection;
  const parsed = await marked.parse(newContent, { gfm: true });
  const docSizeBefore = editor.state.doc.content.size;

  editor
    .chain()
    .command(function (args) {
      const { tr } = args;
      tr.deleteRange(blockStart, blockEnd);
      const fragment = generateJSON(
        parsed,
        getExtensions(() => {})
      );
      const node = editor.schema.nodeFromJSON(fragment);
      tr.insert(blockStart, node.content);
      return true;
    })
    .run();

  const docSizeAfter = editor.state.doc.content.size;
  const insertedSize = docSizeAfter - docSizeBefore;
  const originalBlockSize = blockEnd - blockStart;
  const newContentSize = originalBlockSize + insertedSize;

  return {
    from: blockStart,
    to: blockStart + newContentSize,
  };
}

export function selectNodes(editor: Editor, range: { from: number; to: number }) {
  if (typeof document.createRange !== 'function') {
    return;
  }
  const domFromPos = editor.view.domAtPos(range.from);
  const domToPos = editor.view.domAtPos(range.to);
  const domRange = document.createRange();
  if (typeof domRange.setStart !== 'function' || typeof domRange.setStart !== 'function') {
    return;
  }
  domRange.setStart(domFromPos.node, domFromPos.offset);
  domRange.setEnd(domToPos.node, domToPos.offset);
  if (typeof window.getSelection !== 'function') {
    return;
  }
  const selection = window.getSelection();
  if (
    selection == null ||
    typeof selection.removeAllRanges !== 'function' ||
    typeof selection.addRange !== 'function'
  ) {
    return;
  }
  selection.removeAllRanges();
  selection.addRange(domRange);
}

export function isRectOverlappingNonSelectableElements(rect: DOMRect) {
  const elementsAtPoint = [];
  const pointsToCheck = [
    { x: rect.left, y: rect.top },
    { x: rect.right, y: rect.top },
    { x: rect.left, y: rect.bottom },
    { x: rect.right, y: rect.bottom },
    { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
  ];

  for (const point of pointsToCheck) {
    const elements = document.elementsFromPoint(point.x, point.y);
    elementsAtPoint.push(...elements);
  }

  const uniqueElements = [...new Set(elementsAtPoint)];

  return uniqueElements.some((element) => {
    const style = window.getComputedStyle(element);
    return (
      style.userSelect === 'none' ||
      style.webkitUserSelect === 'none' ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (style as any).MozUserSelect === 'none' ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (style as any).msUserSelect === 'none'
    );
  });
}

function removeHighlightFromNode(node: Node) {
  if (node.isText) {
    const newMarks = node.marks.filter((mark) => mark.type.name !== 'highlight');
    return node.mark(newMarks);
  }

  if (node.content != null && node.content.size > 0) {
    const newContent: Node[] = [];
    // NOTE: forEach because it's a ProseMirror's Fragment and not iterable
    // eslint-disable-next-line unicorn/no-array-for-each
    node.content.forEach(function (child) {
      newContent.push(removeHighlightFromNode(child));
    });
    return node.copy(Fragment.from(newContent));
  }

  return node;
}

export function removeHighlightFromSlice(slice: Slice) {
  const newNodes: Node[] = [];
  // NOTE: forEach because it's a ProseMirror's Fragment and not iterable
  // eslint-disable-next-line unicorn/no-array-for-each
  slice.content.forEach(function (node) {
    newNodes.push(removeHighlightFromNode(node));
  });
  return new Slice(Fragment.from(newNodes), slice.openStart, slice.openEnd);
}

export function removeHighlightMarks(node: Record<string, any> | null) {
  if (node == null) {
    return null;
  }
  const cleanNode = { ...node };
  if (cleanNode.marks != null) {
    cleanNode.marks = (cleanNode.marks as { type: string }[]).filter(
      (mark) => mark.type !== 'highlight'
    );
  }
  if (cleanNode.content != null) {
    cleanNode.content = (cleanNode.content as any[]).map(removeHighlightMarks);
  }
  return cleanNode;
}

export function populateSelectedNodes(editor: Editor) {
  const { state } = editor;
  const { selection } = state;
  const selectedContent = selection.content();
  const cleanSlice = removeHighlightFromSlice(selectedContent);

  const selectedNodes: string[] = [];
  // NOTE: forEach because it's a ProseMirror's Fragment and not iterable
  // eslint-disable-next-line unicorn/no-array-for-each
  cleanSlice.content.forEach(function (node) {
    selectedNodes.push(JSON.stringify(node));
  });
  const nodesBeforeSelection: string[] = [];
  if (selection.from > 0) {
    const slicesBeforeSelection = editor.state.doc.slice(0, selection.from);
    if (slicesBeforeSelection != null) {
      // NOTE: forEach because it's a ProseMirror's Fragment and not iterable
      // eslint-disable-next-line unicorn/no-array-for-each
      slicesBeforeSelection.content.forEach(function (node) {
        nodesBeforeSelection.push(JSON.stringify(node));
      });
    }
  }
  const nodesAfterSelection: string[] = [];
  if (
    typeof state.doc.lastChild?.nodeSize === 'number' &&
    selection.to < state.doc.content.size - state.doc.lastChild.nodeSize
  ) {
    const slicesAfterSelection = state.doc.slice(
      selection.to,
      state.doc.content.size - state.doc.lastChild.nodeSize
    );
    if (slicesAfterSelection != null) {
      // NOTE: forEach because it's a ProseMirror's Fragment and not iterable
      // eslint-disable-next-line unicorn/no-array-for-each
      slicesAfterSelection.content.forEach(function (node) {
        nodesAfterSelection.push(JSON.stringify(node));
      });
    }
  }
  return { selectedNodes, nodesBeforeSelection, nodesAfterSelection };
}

export function highlightFocusWithMark(editor: Editor) {
  // add mark with bg color of lavander.100
  editor.chain().focus().toggleHighlight({ color: '#dfe6ff' }).run();
}

export function removeHighlight(editor: Editor, range: { from: number; to: number }) {
  const { state, view } = editor;
  if (state.schema.marks?.highlight == null) {
    captureException(
      new InternalServerError('highlight extension not found'),
      {
        file: 'shared/tiptap/doc-utilities.ts',
        method: 'removeHighlight',
        infoMessage: 'highlight extension not found',
      },
      trackError
    );
  }
  const removeHighlight = state.tr.removeMark(range.from, range.to, state.schema.marks.highlight);
  view.dispatch(removeHighlight);
}

export function detectElement(element: Node | undefined | null) {
  if (element == null) {
    return 'paragraph';
  }
  let { name } = element.type;
  if (name === 'heading') {
    name += element.attrs.level ?? '1';
  }
  return name;
}
